import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import Logo from './Logo';

const Footer = () => {
  const navigate = useNavigate();

  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    e.preventDefault();
    
    // Navigate to the new route
    navigate(to);
    
    // After navigation, scroll to top smoothly
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <footer className="bg-brand-blue text-white">
      <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center mb-4">
              <Logo showText={false} className="w-8 h-8 mr-2" />
              <span className="text-xl font-bold">  United eLoans</span>
            </div>
            <p className="text-sm text-emerald-100 mb-4">
              Your trusted partner for quick and reliable financial solutions. We're here to help you manage your financial needs with ease.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <a 
                  href="/about-us" 
                  className="hover:text-blue-200 transition-colors"
                  onClick={(e) => handleNavigation(e, '/about-us')}
                >
                  About Us
                </a>
              </li>
              <li>
                <a 
                  href="/how-it-works" 
                  className="hover:text-blue-200 transition-colors"
                  onClick={(e) => handleNavigation(e, '/how-it-works')}
                >
                  How It Works
                </a>
              </li>
              <li>
                <a 
                  href="/why-us" 
                  className="hover:text-blue-200 transition-colors"
                  onClick={(e) => handleNavigation(e, '/why-us')}
                >
                  Why Choose Us
                </a>
              </li>
              <li>
                <a 
                  href="/faq" 
                  className="hover:text-blue-200 transition-colors"
                  onClick={(e) => handleNavigation(e, '/faq')}
                >
                  FAQ
                </a>
              </li>
              <li>
                <a 
                  href="/contact-us" 
                  className="hover:text-blue-200 transition-colors"
                  onClick={(e) => handleNavigation(e, '/contact-us')}
                >
                  Contact Us
                </a>
              </li>
            </ul>
          </div>

        

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-center">
                <Mail className="h-5 w-5 mr-2" />
                <span>info@unitedeloan.com</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="mt-8 pt-6 border-t border-blue-400">
          <div className="text-xs text-blue-100 leading-relaxed space-y-3">
            <p>
              unitedeloan.com (the "website") is not an offer or solicitation to lend. The website only provides a service and is not an agent, representative, or broker of any lender and does not endorse or charge you for any loan or product. The website operators are not lenders, do not make loans of any type, and do not make credit decisions. The website collects personal information provided by you and forwards it to partners in our lender network. You are under no obligation to use this website or service to initiate, contact, nor apply for credit or any loan product with any service provider or lender.
            </p>
            <p>
              If you have any questions, contact your lender directly for details, questions, or concerns regarding your loan or credit product. Short-term cash loans are meant to provide you with short-term financing to solve immediate cash needs and should not be considered a long-term solution. This service and lenders are not available in all states. Lenders may perform credit checks with the three credit reporting bureaus: Experian, Equifax, or Trans Union or may perform alternative credit checks or consumer reports through alternative providers.
            </p>
            <p>
              By submitting your request, you acknowledge, agree, and authorize that (a) your information may be sent to lenders and/or third-party partners on your behalf, and (b) such lenders may obtain consumer reports and related information about you from one or more consumer reporting agencies, such as TransUnion, Experian and Equifax to evaluate your creditworthiness.
            </p>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-blue-400">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="text-sm text-blue-100">
              © United eLoans. All rights reserved.
            </div>
            <div className="text-sm text-blue-100 md:text-right">
              <a 
                href="/privacy-policy" 
                className="hover:text-blue-200 mr-4 transition-colors"
                onClick={(e) => handleNavigation(e, '/privacy-policy')}
              >
                Privacy Policy
              </a>
              <a 
                href="/terms-and-conditions" 
                className="hover:text-blue-200 mr-4 transition-colors"
                onClick={(e) => handleNavigation(e, '/terms-and-conditions')}
              >
                Terms and Conditions
              </a>
              <a 
                href="/disclaimer" 
                className="hover:text-blue-200 mr-4 transition-colors"
                onClick={(e) => handleNavigation(e, '/disclaimer')}
              >
                Disclaimer
              </a>
              <a 
                href="/do-not-sell" 
                className="hover:text-blue-200 mr-4 transition-colors"
                onClick={(e) => handleNavigation(e, '/do-not-sell')}
              >
                Do Not Sell My Information
              </a>
              <a 
                href="/unsubscribe" 
                className="hover:text-blue-200 mr-4 transition-colors"
                onClick={(e) => handleNavigation(e, '/unsubscribe')}
              >
                Unsubscribe
              </a>
              <a 
                href="/fraud-prevention" 
                className="hover:text-blue-200 transition-colors"
                onClick={(e) => handleNavigation(e, '/fraud-prevention')}
              >
                Fraud Prevention
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;